import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AddExpenseModal } from './AddExpenseModal';
import { ExpenseRecordsModal } from './ExpenseRecordsModal';
import { ConfirmDialog } from './ConfirmDialog';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import { BUDGET_TYPE } from '../constants/otrEnums';
import { errMsg } from '../utils/errorMessage';
import { hasTravelStarted } from '../utils/otrCalculations';
import { formatOtrDate } from '../utils/formatOtrDate';
import {
  getExpenseLines,
  submitTravelExpenses,
  submitTravelExpensesClaim,
  deactivateExpenseLine,
  updateExpenseLine,
  downloadExpenseLineDocument,
} from '../../../services/outstationTravelService';


const formatBudget = (head, tripDays, currencySymbol) => {
  if (head.budgetType === BUDGET_TYPE.AT_ACTUALS) return 'At actuals';
  if (head.budgetType === BUDGET_TYPE.PER_DAY) {
    const total = (head.defaultAmount || 0) * (tripDays || 1);
    return `${currencySymbol}${total.toLocaleString()}`;
  }
  return `${currencySymbol}${(head.defaultAmount || 0).toLocaleString()}`;
};

// Browsers only render a blob inline when it has a real MIME type; the API may
// return application/octet-stream (or nothing), which opens a blank tab or
// triggers a download. Detect the type from the file signature instead.
const withViewableType = async (blob) => {
  const known = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
  if (known.includes(blob.type)) return blob;

  const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  const hex = Array.from(head, (b) => b.toString(16).padStart(2, '0')).join('');
  let type = blob.type || 'application/pdf';
  if (hex.startsWith('25504446')) type = 'application/pdf';
  else if (hex.startsWith('ffd8ff')) type = 'image/jpeg';
  else if (hex.startsWith('89504e47')) type = 'image/png';
  else if (hex.startsWith('47494638')) type = 'image/gif';
  else if (hex.startsWith('52494646')) type = 'image/webp';
  return new Blob([blob], { type });
};

const handleViewDocument = async (expenseId, onError) => {
  // Open the tab synchronously inside the click so the popup blocker allows
  // it; the document is pointed at it once the download finishes.
  const viewer = window.open('', '_blank');

  try {
    const result = await downloadExpenseLineDocument(expenseId);
    const blob = result?.blob;

    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error(result?.message || 'No document found for this expense.');
    }

    const documentUrl = window.URL.createObjectURL(await withViewableType(blob));

    if (viewer) {
      viewer.location.href = documentUrl;
    } else {
      // Popup was blocked: fall back to a normal link click.
      const a = document.createElement('a');
      a.href = documentUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    // Release the temporary browser URL after the tab has loaded it.
    setTimeout(() => window.URL.revokeObjectURL(documentUrl), 60000);
  } catch (err) {
    console.error('Failed to open expense document', err);
    if (viewer) viewer.close();
    onError?.(err?.message || 'Unable to open the document. Please try again.');
  }
};


export const DailyExpenseDashboard = ({
  requestId,
  policyHeads = [],
  tripDays,
  currencySymbol = '₹',
  advanceRequested = 0,
  canAddExpenses: statusAllowsExpenses = true,
  travelStartDate,
  onClaimSubmitted,
}) => {
  const { t } = useTranslation();
  // Entries are locked until the travel start date (today >= start date).
  const tripStarted = hasTravelStarted(travelStartDate);
  const canAddExpenses = statusAllowsExpenses && tripStarted;
  const [lines, setLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addDefaultHeadId, setAddDefaultHeadId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [viewHead, setViewHead] = useState(null);
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [addError, setAddError] = useState('');
  const [pageError, setPageError] = useState('');
  const [deletingLineId, setDeletingLineId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type: 'submit' | 'delete', record? }
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const [editingLine, setEditingLine] = useState(null);

  const loadLines = useCallback(async () => {
    if (!requestId) {
      setLoadingLines(false);
      return;
    }
    setLoadingLines(true);
    setPageError('');
    try {
      const rows = await getExpenseLines({ requestId });
      setLines(Array.isArray(rows) ? rows.filter((l) => l.isActive !== false) : []);
    } catch (err) {
      console.error('Failed to load expense records', err);
      setPageError(errMsg(err, 'Could not load the expense records.'));
    } finally {
      setLoadingLines(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  const expenseForHead = (headId) =>
    lines
      .filter(l => Number(l.expenseHeadId) === Number(headId))
      .reduce((sum, l) => sum + Number(l.amount || 0), 0);

  const recordsForHead = (headId) =>
    lines.filter(l => Number(l.expenseHeadId) === Number(headId));

  const openAdd = (headId = null) => {
    setEditingLine(null);
    setAddDefaultHeadId(headId);
    setAddError('');
    setAddModalOpen(true);
  };

  const openEdit = (record) => {
    setViewHead(null);
    setEditingLine(record);
    setAddError('');
    setAddModalOpen(true);
  };

  const handleAddExpense = async (line) => {
    if (!canAddExpenses) return;
    setIsSaving(true);
    setAddError('');
    try {
      if (editingLine) {
        const lineId = editingLine.expenseLineId ?? editingLine.expenseId ?? editingLine.id;
        await updateExpenseLine(lineId, line);
      } else {
        await submitTravelExpenses(requestId, [line]);
      }
      await loadLines();
      setAddModalOpen(false);
      showToast('Expense added successfully.');
      setEditingLine(null);
    } catch (err) {
      console.error('Failed to save expense', err);
      setAddError(errMsg(err, editingLine ? 'Could not update the expense.' : 'Could not add the expense.'));
    } finally {
      setIsSaving(false);
    }
  };

  // "Delete" = the server marks the line inactive; it then drops out of the
  // active list that both employee and manager see.
  const handleDeleteLine = async (record) => {
    const lineId = record?.expenseLineId ?? record?.expenseId ?? record?.id;
    if (!canAddExpenses || !lineId || deletingLineId) return;
    setDeletingLineId(lineId);
    setPageError('');
    try {
      await deactivateExpenseLine(record);
      await loadLines();
      showToast('Expense record deleted.');
    } catch (err) {
      console.error('Failed to delete expense line', err);
      showToast(errMsg(err, 'Could not delete the expense record.'), 'error');
    } finally {
      setDeletingLineId(null);
      setConfirm(null);
    }
  };

  // Submits every expense line filed against the request in one go.
  const handleSubmitClaim = async () => {
    if (!canAddExpenses || lines.length === 0 || submittingClaim) return;
    setSubmittingClaim(true);
    setPageError('');
    try {
      await submitTravelExpensesClaim(requestId);
      showToast('Expenses submitted for approval.');
      if (onClaimSubmitted) await onClaimSubmitted();
    } catch (err) {
      console.error('Failed to submit expenses', err);
      showToast(errMsg(err, 'Could not submit expenses.'), 'error');
    } finally {
      setSubmittingClaim(false);
      setConfirm(null);
    }
  };

  // Summary shown at the top: advance, budget and spend so far.
  const totalSpent = lines.reduce((sum, l) => sum + Number(l.amount || 0), 0);
  const hasActualsHead = policyHeads.some((h) => h.budgetType === BUDGET_TYPE.AT_ACTUALS);
  const totalBudget = policyHeads.reduce((sum, h) => {
    if (h.budgetType === BUDGET_TYPE.AT_ACTUALS) return sum;
    const amt = Number(h.defaultAmount || 0);
    return sum + (h.budgetType === BUDGET_TYPE.PER_DAY ? amt * (tripDays || 1) : amt);
  }, 0);
  const balance = totalSpent - Number(advanceRequested || 0);
  const money = (v) => `${currencySymbol}${Number(v || 0).toLocaleString()}`;
  const summaryStats = [
    { label: t('outstation.advanceRequested'), value: money(advanceRequested) },
    { label: 'Total Budget', value: money(totalBudget), note: hasActualsHead ? 'Excludes at-actuals heads' : '' },
    { label: 'Total Spent', value: loadingLines ? '...' : money(totalSpent) },
    {
      label: 'Balance',
      value: loadingLines ? '...' : money(Math.abs(balance)),
      note: balance > 0 ? 'Payable to you' : balance < 0 ? 'To be returned to company' : 'Settled',
    },
  ];

  const headBudget = (head) => {
    if (head.budgetType === BUDGET_TYPE.AT_ACTUALS) return null;
    const amt = Number(head.defaultAmount || 0);
    return head.budgetType === BUDGET_TYPE.PER_DAY ? amt * (tripDays || 1) : amt;
  };

  return (
    <div>
      {pageError && (
        <div className="mb-6 p-4 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 text-sm text-[var(--color-error)] font-medium">
          {pageError}
        </div>
      )}

      {statusAllowsExpenses && !tripStarted && (
        <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 font-medium">
          Expenses can be added only once your travel starts
          {travelStartDate ? ` (from ${formatOtrDate(travelStartDate)})` : ''}.
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-1">{t('outstation.dailyExpenseEntry')}</h2>
          <p className="text-sm text-[var(--color-text-secondary)]">Budget and spend for each expense head, with the overall report below.</p>
        </div>
        {canAddExpenses && (
          <div className="flex flex-wrap gap-3 sm:shrink-0">
            <button
              type="button"
              onClick={() => openAdd(null)}
              className="h-10 px-5 rounded-xl border border-[var(--color-primary)] text-[var(--color-primary)] text-[12px] font-bold uppercase tracking-wider hover:bg-[var(--color-primary)]/5 transition-all"
            >
              + Add
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ type: 'submit' })}
              disabled={lines.length === 0 || submittingClaim || loadingLines}
              className="h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white text-[12px] font-bold uppercase tracking-wider shadow-sm hover:bg-[var(--color-accent)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingClaim ? t('outstation.submitting') : 'Submit Expenses'}
            </button>
          </div>
        )}
      </div>

      {policyHeads.length === 0 ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          Expense heads will appear here once a city tier is assigned to this request.
        </p>
      ) : (
        <div className="rounded-2xl border border-[var(--color-border)] overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left border-collapse">
              <thead>
                <tr className="bg-[var(--color-workspace)]/50 border-b border-[var(--color-border)]">
                  <th className="px-5 py-3 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Expense Head</th>
                  <th className="px-5 py-3 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-right">Budget</th>
                  <th className="px-5 py-3 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-right">Total Spent</th>
                  <th className="px-5 py-3 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-right">Records</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {policyHeads.map((head) => {
                  const spent = expenseForHead(head.headId);
                  const budget = headBudget(head);
                  const over = budget !== null && budget > 0 && spent > budget;
                  return (
                    <tr key={head.headId} className="hover:bg-[var(--color-workspace)]/20 transition-colors">
                      <td className="px-5 py-4">
                        <div className="text-sm font-bold text-foreground">{head.headName}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] mt-0.5">
                          {head.budgetType === BUDGET_TYPE.PER_DAY ? 'Per day' : head.budgetType === BUDGET_TYPE.AT_ACTUALS ? 'At actuals' : 'Lumpsum'}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="text-sm font-bold text-[var(--color-primary)]">
                          {formatBudget(head, tripDays, currencySymbol)}
                        </div>
                        {head.budgetType === BUDGET_TYPE.PER_DAY && (
                          <div className="text-[11px] text-[var(--color-text-secondary)] mt-0.5">
                            {money(head.defaultAmount)} × {tripDays || 1} {(tripDays || 1) === 1 ? 'day' : 'days'}
                          </div>
                        )}
                      </td>
                      <td className={`px-5 py-4 text-sm font-bold text-right whitespace-nowrap ${over ? 'text-[var(--color-error)]' : 'text-foreground'}`}>
                        {loadingLines ? '...' : money(spent)}
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setViewHead(head)}
                          className="text-[12px] font-bold text-[var(--color-primary)] hover:underline"
                        >
                          View ({recordsForHead(head.headId).length}) ›
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[var(--color-workspace)]/40 border-t border-[var(--color-border)]">
                  <td className="px-5 py-3 text-sm font-bold text-foreground">Total</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <div className="text-sm font-bold text-[var(--color-primary)]">{money(totalBudget)}</div>
                    {hasActualsHead && (
                      <div className="text-[11px] font-normal text-[var(--color-text-secondary)] mt-0.5">Excludes at-actuals heads</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm font-bold text-right text-foreground whitespace-nowrap">{loadingLines ? '...' : money(totalSpent)}</td>
                  <td className="px-5 py-3 text-right text-[11px] font-bold text-[var(--color-text-secondary)]">{lines.length} total</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">Overall Report</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {summaryStats.map((st) => (
            <div key={st.label} className="p-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-workspace)]/30">
              <div className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">{st.label}</div>
              <div className="text-xl font-bold text-foreground">{st.value}</div>
              {st.note && !loadingLines && (
                <div className="text-[11px] text-[var(--color-text-secondary)] mt-1">{st.note}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <AddExpenseModal
        open={addModalOpen}
        onOpenChange={(open) => {
          setAddModalOpen(open);
          if (!open) setEditingLine(null);
        }}
        editRecord={editingLine}
        expenseHeads={policyHeads}
        defaultHeadId={addDefaultHeadId}
        onAdd={handleAddExpense}
        isSaving={isSaving}
        submitError={addError}
        currencySymbol={currencySymbol}
      />

      <ExpenseRecordsModal
        open={!!viewHead}
        onOpenChange={(open) => !open && setViewHead(null)}
        headName={viewHead?.headName}
        records={viewHead ? recordsForHead(viewHead.headId) : []}
        isLoading={loadingLines}
        currencySymbol={currencySymbol}
        onViewDocument={(id) => handleViewDocument(id, (m) => showToast(m, 'error'))}
        canDelete={canAddExpenses}
        onDelete={(record) => setConfirm({ type: 'delete', record })}
        onEdit={openEdit}
        deletingLineId={deletingLineId}
      />

      <ConfirmDialog
        open={confirm?.type === 'submit'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={t('outstation.submitExpensesTitle')}
        message={t('outstation.submitExpensesMessage', { count: lines.length })}
        confirmLabel={t('outstation.submit')}
        loadingLabel={t('outstation.submitting')}
        isLoading={submittingClaim}
        onConfirm={handleSubmitClaim}
      />

      <ConfirmDialog
        open={confirm?.type === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Delete expense record?"
        message="This record will no longer appear in the expense report."
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        destructive
        isLoading={deletingLineId != null}
        onConfirm={() => handleDeleteLine(confirm?.record)}
      />

      <LeaveActionToast toast={toast} />
    </div>
  );
};